import { useState } from 'react'
import { gql } from 'graphql-request'
import { useForm } from 'react-hook-form'
import { AlertTriangle, Info, FileText, Trash2, ChevronDown, Check, School, Globe, Lock, X, Loader2 } from 'lucide-react'

import { useMutation, useQuery } from '@hooks/graphql'
import { useCurrentUser } from '@contexts/currentUser'
import { Button } from '@/components/ui/button'
import TextEditor from '@components/TextEditor'
import MultipleFileInput from '@components/MultipleFileInput'
import Tooltip from '@components/Tooltip'
import Collapsible from '@components/Collapsible'

import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

import { useSubscription } from '../../../hooks/subscription'

import TutorImage from './TutorImage'
import DeleteTutorDialog from './DeleteTutorDialog'
import { Badge } from '@/components/ui/badge'

const UPDATE_TUTOR_MUTATION = gql`
  mutation updateTutor($input: UpdateInput!) {
    updateTutor(input: $input) {
      tutor {
        id
        name
        createdAt
        description
        summary
        socratic
        suggestionsEnabled
        stemModeEnabled
        tags {
          id
          name
        }
      }
    }
  }
`

const UPLOAD_DOCUMENTS_MUTATION = gql`
  mutation uploadDocuments($input: UploadDocumentsInput!) {
    uploadDocuments(input: $input) {
      files {
        url
      }
    }
  }
`

const DELETE_DOCUMENT_MUTATION = gql`
  mutation deleteDocument($input: DeleteDocumentInput!) {
    deleteDocument(input: $input) {
      success
    }
  }
`

const TAGS_QUERY = gql`
  query tutorTags {
    tutorTags {
      id
      slug
      name
      color
    }
  }
`

const AI_MODELS_QUERY = gql`
  query aiModels {
    aiModels {
      model
      modelFriendly
      company
      vision
      selectable
    }
  }
`

const Settings = ({
  tutorId,
  name,
  visibility,
  description,
  summary,
  socratic,
  suggestionsEnabled,
  stemModeEnabled,
  knowledgeBaseEnabled,
  tagIds,
  isOwner,
  refetch,
  imageGenerationStatus,
  imageGenerationStartedAt,
  imageUrl,
  documents,
  model
}) => {
  const { verificationStatus } = useSubscription()
  const [showFileSizeError, setShowFileSizeError] = useState(false)
  const [deletingDocumentId, setDeletingDocumentId] = useState(null)

  const form = useForm({
    mode: 'onTouched',
    defaultValues: {
      visibility,
      name,
      description,
      summary,
      socratic,
      stemModeEnabled,
      knowledgeBaseEnabled,
      suggestionsEnabled,
      tagIds,
      model
    }
  })

  const { control, handleSubmit, watch, formState: { errors }, setError, trigger } = form

  const { mutate: updateTutor, isLoading: isUpdating, isSuccess } = useMutation({
    gqlMutation: UPDATE_TUTOR_MUTATION,
    onSuccess: () => refetch()
  })

  const { mutate: uploadDocuments, isLoading: isUploading } = useMutation({
    gqlMutation: UPLOAD_DOCUMENTS_MUTATION,
    onSuccess: () => refetch()
  })

  const { mutate: deleteDocument } = useMutation({
    gqlMutation: DELETE_DOCUMENT_MUTATION,
    onSettled: () => {
      setDeletingDocumentId(null)
      refetch()
    }
  })

  const { data: { tutorTags: allTags = [] } = {} } = useQuery({
    queryKey: ['tutorTags'],
    gqlQuery: TAGS_QUERY
  })

  const { data: { aiModels = [] } = {} } = useQuery({
    queryKey: ['aiModels'],
    gqlQuery: AI_MODELS_QUERY
  })

  const { currentMembership } = useCurrentUser()
  const features = currentMembership?.organization?.features || []
  const tutorModelChoiceEnabled = features.includes('tutor_model_choice')

  const onSubmit = data => updateTutor({ input: { ...data, id: tutorId } })

  const knowledgeBaseEnabledInForm = watch('knowledgeBaseEnabled')
  const visibilityForm = watch('visibility')

  const visibilityOptions = {
    PRIVATE: {
      icon: Lock,
      label: 'Private',
      description: 'Only available to you'
    },
    ORGANIZATION: {
      icon: School,
      label: 'Organization',
      description: 'Available to educators within your organization'
    },
    PUBLIC: {
      icon: Globe,
      label: 'Public',
      description: 'Publicly available online for others to discover'
    }
  }

  const CurrentVisibilityIcon = visibilityOptions[visibilityForm]?.icon

  return (
    <div className='h-full'>
      <Form {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className='flex md:flex-row flex-col gap-5 p-5'
        >
          <TutorImage
            tutorId={tutorId}
            isOwner={isOwner}
            imageGenerationStatus={imageGenerationStatus}
            imageGenerationStartedAt={imageGenerationStartedAt}
            imageUrl={imageUrl}
            refetch={refetch}
          />

          <div className='flex flex-col grow space-y-6'>
            <If condition={!isOwner}>
              <p className='w-fit flex items-center leading-tight rounded-lg p-2 bg-gray-200 text-sm'>
                <Lock className='h-5 w-5 mr-1' /> Only the owner can make changes
              </p>
            </If>

            <FormField
              control={control}
              name='name'
              rules={{ required: 'Name is required' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder='Tutor Name' {...field} disabled={!isOwner} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name='description'
              rules={{ required: 'Prompt is required' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Prompt</FormLabel>
                  <FormControl>
                    <TextEditor.Container>
                      <TextEditor
                        className='h-[100px]'
                        id='description'
                        placeholder='Write the prompt for your tutor here...'
                        disabled={!isOwner}
                        onError={message => setError('description', { message })}
                        {...field}
                      />
                      <TextEditor.Actions>
                        <TextEditor.ToolbarToggle />
                      </TextEditor.Actions>
                    </TextEditor.Container>
                  </FormControl>
                  <FormDescription>
                    Describe a subject, lesson, or tutor style. Be creative!
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name='knowledgeBaseEnabled'
              rules={{ validate: value => !value || (value && documents.length > 0) || 'Upload documents to enable knowledge base' }}
              render={({ field }) => (
                <FormItem className='flex flex-col'>
                  <div className='flex flex-row items-center justify-between'>
                    <div className='space-y-0.5 pr-4'>
                      <FormLabel className='text-base'>Knowledge base</FormLabel>
                      <FormDescription>
                        Upload documents to add custom knowledge to your tutor.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={!isOwner}
                      />
                    </FormControl>
                  </div>
                  <FormMessage className='pt-2' />

                  <If condition={field.value}>
                    <div className='flex flex-col'>
                      <If condition={isOwner}>
                        <FormItem>
                          <FormControl>
                            <MultipleFileInput
                              id='documents-upload'
                              className='w-fit'
                              onChange={ev => {
                                setShowFileSizeError(false)
                                const files = Array.from(ev.target.files)
                                if (files.some(file => file.size > 10000000)) {
                                  setShowFileSizeError(true)
                                  return
                                }
                                uploadDocuments({ input: { id: tutorId, files } })
                              }}
                              accept='.pdf'
                              disabled={isUploading}
                              label={isUploading ? 'Uploading...' : 'Upload documents'}
                            />
                          </FormControl>
                          <FormDescription>
                            Files must be in .pdf format and smaller than 10mb. If you upload files, chats with a tutor may include file contents.
                          </FormDescription>
                        </FormItem>
                      </If>

                      <If condition={showFileSizeError}>
                        <p className='text-sm mt-1 text-red-500 font-semibold' role='alert'>File size must be less than 10mb</p>
                      </If>

                      <div className='flex flex-wrap gap-2 mt-3'>
                        <For each='document' of={documents}>
                          <div key={document.id} className='flex items-center gap-2 px-2 py-1 text-sm border rounded-md bg-muted'>
                            <FileText className='h-4 w-4 text-muted-foreground' />
                            <span className='max-w-[150px] truncate' title={document?.file?.filename}>{document?.file?.filename}</span>

                            <If condition={isOwner}>
                              <Button
                                type='button'
                                variant='ghost'
                                size='icon'
                                className='h-5 w-5 text-muted-foreground hover:text-destructive'
                                loading={deletingDocumentId === document.id}
                                onClick={() => {
                                  setDeletingDocumentId(document.id)
                                  deleteDocument({ input: { documentId: document.id } })
                                }}
                                aria-label={`Remove ${document?.file?.filename}`}
                                disabled={deletingDocumentId === document.id}
                              >
                                {deletingDocumentId === document.id ? <></> : <Trash2 className='h-4 w-4' />}
                              </Button>
                            </If>
                          </div>
                        </For>
                      </div>
                    </div>
                  </If>
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name='socratic'
              render={({ field }) => (
                <FormItem className='flex flex-row items-center justify-between'>
                  <div className='space-y-0.5'>
                    <FormLabel className='text-base'>Socratic mode</FormLabel>
                    <FormDescription>
                      This turns on probing questions instead of direct answers.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={!isOwner}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name='stemModeEnabled'
              render={({ field }) => (
                <FormItem className='flex flex-row items-center justify-between'>
                  <div className='space-y-0.5'>
                    <FormLabel className='text-base flex items-center'>
                      STEM mode
                      <Tooltip anchorSelect='#new-feature-pill'>Mathematics mode is now STEM mode, give it a try ✨</Tooltip>
                    </FormLabel>
                    <FormDescription>
                      This turns on accurate calculations and visualisations for STEM related subjects (e.g. mathematics, science, physics, chemistry, etc.).
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      id='stem-mode-enabled'
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={!isOwner}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={control}
              name='suggestionsEnabled'
              render={({ field }) => (
                <FormItem className='flex flex-row items-center justify-between'>
                  <div className='space-y-0.5'>
                    <FormLabel className='text-base'>Suggested questions</FormLabel>
                    <FormDescription>
                      Followup questions will be suggested based on recent messages.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      id='suggestions-enabled'
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={!isOwner}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <If condition={isOwner}>
              <FormField
                control={control}
                name='visibility'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Visibility</FormLabel>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild disabled={!isOwner}>
                        <Button variant='outline' className='w-full sm:w-[350px] justify-between font-normal'>
                          <div className='flex items-center'>
                            {CurrentVisibilityIcon && <CurrentVisibilityIcon className='mr-2 h-4 w-4' />}
                            {visibilityOptions[field.value]?.label || 'Select visibility'}
                          </div>
                          <ChevronDown className='ml-2 h-4 w-4 opacity-50' />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className='w-[350px]'>
                        {Object.entries(visibilityOptions).map(([value, { icon: Icon, label, description }]) => {
                          if (value === 'PUBLIC' && verificationStatus !== 'VERIFIED') {
                            return null
                          }
                          const isSelected = field.value === value
                          return (
                            <DropdownMenuItem
                              key={value}
                              onSelect={() => field.onChange(value)}
                              className={isSelected ? 'bg-accent text-accent-foreground' : ''}
                            >
                              <Icon className='mr-3 h-5 w-5 shrink-0' />
                              <div>
                                <p className='leading-tight font-medium'>{label}</p>
                                <p className='text-xs text-muted-foreground leading-tight'>{description}</p>
                              </div>
                            </DropdownMenuItem>
                          )
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <If condition={knowledgeBaseEnabledInForm && visibilityForm !== 'PRIVATE'}>
                <Alert variant='destructive'>
                  <AlertTriangle className='h-4 w-4' />
                  <AlertTitle>Heads up!</AlertTitle>
                  <AlertDescription>
                    Documents you upload will visible to educators {visibilityForm === 'ORGANIZATION' ? 'within your organization' : 'publicly'}.
                  </AlertDescription>
                </Alert>
              </If>

              <Collapsible label='Advanced settings'>
                <div className='space-y-6 pt-4'>

                  <If condition={tutorModelChoiceEnabled}>
                    <FormField
                      control={control}
                      name='model'
                      render={({ field }) => {
                        const modelOption = aiModels.find(m => m.model === field.value)
                        return (
                          <FormItem>
                            <FormLabel>
                              AI Model <Badge variant='outline'>Experimental feature</Badge>
                            </FormLabel>
                            <FormControl>
                              <Select onValueChange={field.onChange} value={field.value || ''}>
                                <SelectTrigger id='' className='mt-1 w-[350px]'>
                                  <SelectValue placeholder='Select a model'>
                                    <Choose>
                                      <When condition={modelOption}>
                                        <div className='flex items-center'>
                                          <img src={`/ai_companies/${modelOption.company.toLowerCase()}.svg`} className='size-6 mr-3 shrink-0' />
                                          <p className='leading-none'>{modelOption.modelFriendly}</p>
                                        </div>
                                      </When>
                                      <Otherwise>Select a model</Otherwise>
                                    </Choose>
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <For each='modelOption' of={aiModels}>
                                    <If condition={modelOption.selectable || modelOption.value === field.value}>
                                      <SelectItem value={modelOption.model}>
                                        <div className='flex mt-1'>
                                          <img src={`/ai_companies/${modelOption.company.toLowerCase()}.svg`} className='size-6 mr-3 shrink-0' />
                                          <div>
                                            <p className='leading-none'>{modelOption.modelFriendly}</p>
                                            <small className='leading-none'>{modelOption.company}</small>
                                          </div>
                                        </div>
                                      </SelectItem>
                                    </If>
                                  </For>
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormDescription>
                              Please note that this is an experimental feature. If you need help picking a model, please check our <a target='_blank' href='https://www.mindjoy.com/model-selection' className='underline' rel='noreferrer'>model guide</a>.
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )
                      }}
                    />
                  </If>

                  <FormField
                    control={control}
                    name='summary'
                    rules={{ maxLength: { value: 200, message: 'Description can\'t be longer than 200 characters' } }}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Short Description</FormLabel>
                        <FormControl>
                          <Textarea
                            className='h-[60px] resize-none bg-background dark:bg-background-dark ring-0 focus-visible:ring-0'
                            placeholder='Write a short description of your tutor (max 200 characters)'
                            disabled={!isOwner}
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Visible when you share your tutor or assign it to a classroom.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={control}
                    name='tagIds'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tags</FormLabel>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild disabled={!isOwner}>
                            <Button variant='outline' className='w-full sm:w-[350px] justify-start font-normal'>
                              <span className='truncate'>
                                {field.value?.length > 0
                                  ? allTags.filter(tag => field.value.includes(tag.id)).map(tag => tag.name).join(', ')
                                  : 'Select tags'}
                              </span>
                              <ChevronDown className='ml-auto h-4 w-4 opacity-50' />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className='w-[350px]'>
                            {allTags.map(tag => (
                              <DropdownMenuCheckboxItem
                                key={tag.id}
                                checked={field.value?.includes(tag.id)}
                                onCheckedChange={checked => {
                                  const currentTags = field.value || []
                                  const newTags = checked
                                    ? [...currentTags, tag.id]
                                    : currentTags.filter(id => id !== tag.id)
                                  field.onChange(newTags)
                                }}
                                onSelect={e => e.preventDefault()}
                              >
                                {tag.name}
                              </DropdownMenuCheckboxItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <FormDescription>
                          Add tags to make your tutor more discoverable. Note: doesn't apply if visibility is set to private.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </Collapsible>

              <div className='flex justify-end items-center pt-6 gap-2'>
                {isSuccess && <Check className='h-5 w-5 text-green-600' />}
                <Button
                  disabled={isUpdating || !isOwner}
                  type='submit'
                  className='h-10 px-4 py-2'
                >
                  {isUpdating
                    ? (
                      <>
                        <Loader2 className='mr-2 h-5 w-5 animate-spin' />
                        Saving...
                      </>
                      )
                    : 'Save Changes'}
                </Button>
              </div>

              <div className='border border-destructive rounded-lg mt-8'>
                <div className='p-5 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between'>
                  <div>
                    <h4 className='font-semibold'>Delete tutor</h4>
                    <p className='text-sm leading-tight text-muted-foreground mt-1'>
                      Delete this tutor and all its associated messages, sessions and student data. This action cannot be undone.
                    </p>
                  </div>

                  <DeleteTutorDialog tutorId={tutorId} isOwner={isOwner} />
                </div>
              </div>
            </If>
          </div>
        </form>
      </Form>
    </div>
  )
}

export default Settings
